import { CloseIcon, PlusIcon } from "@cynthai/icons";
import type { KeyboardEvent } from "react";
import { useMemo, useRef, useState } from "react";
import { useCollator, useFilter } from "react-aria";
import type { Key } from "react-aria-components";
import {
  Button as RAButton,
  ComboBox as RAComboBox,
  ComboBoxValue as RAComboBoxValue,
  Group as RAGroup,
  Input as RAInput,
  Popover as RAPopover,
  Tag as RATag,
  TagGroup as RATagGroup,
  TagList as RATagList,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import InputErrorMessage from "./InputErrorMessage";
import { m } from "./paraglide/messages";
import { SelectLabel, SelectListbox } from "./Select";
import Spinner from "./Spinner";
import type { SelectItem, SlotClassNames } from "./types";

const creatableComboBox = tv({
  slots: {
    base: "flex flex-col gap-0.5",
    inputWrapper: `
      flex min-h-12 flex-wrap items-center gap-2 rounded-lg border
      border-transparent bg-secondary px-4 py-2 text-text-primary
      data-disabled:cursor-not-allowed data-disabled:opacity-50
      data-focus-within:border-border
      data-invalid:border-danger
    `,
    tag: `
      inline-flex max-w-full items-center gap-1 rounded-md bg-primary/5 px-2
      py-0.5 text-sm text-primary/80 outline-none
      data-focus-visible:ring-1 data-focus-visible:ring-primary
    `,
    input: `
      min-w-24 flex-1 bg-transparent text-sm outline-none
      placeholder:text-text-secondary
      disabled:cursor-not-allowed
    `,
    popover:
      "flex w-(--trigger-width) min-w-[min(16rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden",
  },
});

// numeric so it can never collide with a user-typed string key
const CREATE_KEY = -1;

type CreatableComboBoxLabelingProps =
  | { label: string; "aria-label"?: string }
  | { label?: undefined; "aria-label": string };

export type CreatableComboBoxItem = { value: string; label: string };

type MultipleSelectionProps = {
  selectionMode?: "multiple";
  /** Input mode shows suggestions only while a non-empty name is typed. */
  menuTrigger?: "focus" | "input";
  /** existing names to pick from; typed names outside this list can be created */
  items: readonly string[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  onCreate?: never;
};

type SingleSelectionProps = {
  selectionMode: "single";
  items: readonly CreatableComboBoxItem[];
  value: string | null;
  onChange: (value: string) => void;
  /**
   * persists a typed name that matches no item. the returned item gets
   * selected and its label shows once it is in `items`. on rejection nothing
   * is selected and the typed name stays for a retry; reporting the error is
   * up to the caller.
   */
  onCreate: (name: string) => Promise<CreatableComboBoxItem>;
};

type CreatableComboBoxBaseProps = {
  placeholder?: string;
  /** presence renders the message and marks the field invalid */
  errorMessage?: string;
  isDisabled?: boolean;
  classNames?: SlotClassNames<ReturnType<typeof creatableComboBox>>;
} & CreatableComboBoxLabelingProps;

export type CreatableComboBoxProps = CreatableComboBoxBaseProps &
  (MultipleSelectionProps | SingleSelectionProps);

function useCreatableEntries(
  options: readonly CreatableComboBoxItem[],
  inputValue: string,
) {
  const collator = useCollator({ sensitivity: "base" });
  const createName = inputValue.trim();

  const entries = useMemo<SelectItem<Key>[]>(() => {
    const exists = options.some(
      (option) => collator.compare(option.label, createName) === 0,
    );
    if (createName === "" || exists) return [...options];
    return [
      ...options,
      {
        value: CREATE_KEY,
        // the filter matches rows on their label, and the trimmed input
        // always contains itself
        label: createName,
        renderContent: () => (
          <span className="flex items-center gap-2">
            <PlusIcon aria-hidden width={16} height={16} />
            {m.create_named({ name: createName })}
          </span>
        ),
      },
    ];
  }, [options, createName, collator]);

  return {
    entries,
    createName,
    // RAC builds the collection a render behind the input, so its own
    // emptiness check sees a stale create row filtered out and closes the
    // popover; typed text always yields a create row or an exact match
    allowsEmptyCollection: createName !== "" || options.length > 0,
  };
}

function useContainsFilter() {
  const { contains } = useFilter({ sensitivity: "base" });
  return (textValue: string, input: string) =>
    contains(textValue, input.trim());
}

// with the popover closed the combobox lets enter through to the form
function preventEnterSubmit(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key === "Enter") e.preventDefault();
}

/**
 * Text field that filters `items` and offers a `Create "<name>"` row for a
 * typed name that doesn't exist yet. In multiple mode creating only adds the
 * name to the value and persisting it is up to the caller; in single mode the
 * caller persists it through `onCreate`.
 */
export default function CreatableComboBox(
  props: Readonly<CreatableComboBoxProps>,
) {
  return props.selectionMode === "single" ? (
    <SingleCreatableComboBox {...props} />
  ) : (
    <MultipleCreatableComboBox {...props} />
  );
}

function MultipleCreatableComboBox({
  items,
  value,
  onChange,
  menuTrigger = "focus",
  label,
  "aria-label": ariaLabel,
  placeholder,
  errorMessage,
  isDisabled = false,
  classNames,
}: Readonly<CreatableComboBoxBaseProps & MultipleSelectionProps>) {
  const [inputValue, setInputValue] = useState("");
  const isTabbingRef = useRef(false);
  const filter = useContainsFilter();
  const collator = useCollator({ sensitivity: "base" });
  const accessibleLabel = label ?? ariaLabel;
  const isInvalid = !!errorMessage;
  const styles = creatableComboBox();

  // selected names stay in the collection even when they aren't options, so
  // ComboBoxValue can resolve them and the list can toggle them off
  const options = useMemo(
    () =>
      [...new Set([...items, ...value])].map((name) => ({
        value: name,
        label: name,
      })),
    [items, value],
  );
  const { entries, createName, allowsEmptyCollection } = useCreatableEntries(
    options,
    inputValue,
  );

  const handleChange = (keys: Key[]) => {
    // RAC commits tab like enter, toggling whichever row is highlighted; in a
    // multi-select that would drop a selected tag, so tab only leaves the field
    if (isTabbingRef.current) {
      isTabbingRef.current = false;
      return;
    }
    // on blur RAC re-emits the selection it held before the blur, which
    // would overwrite the name handleBlur just committed
    const isEcho =
      keys.length === value.length && keys.every((key, i) => key === value[i]);
    if (isEcho) return;
    const names = keys.filter((key): key is string => key !== CREATE_KEY);
    // the create row is selectable like any other row, so both click and
    // enter arrive here rather than through an item action
    onChange(keys.includes(CREATE_KEY) ? [...names, createName] : names);
    setInputValue("");
  };

  const handleBlur = () => {
    isTabbingRef.current = false;
    setInputValue("");
    if (createName === "") return;
    const existing = options.find(
      (option) => collator.compare(option.label, createName) === 0,
    );
    const name = existing?.value ?? createName;
    if (!value.includes(name)) onChange([...value, name]);
  };

  const handleKeyDownCapture = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      menuTrigger === "input" &&
      createName === "" &&
      (e.key === "ArrowDown" || e.key === "ArrowUp")
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    preventEnterSubmit(e);

    if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <RAComboBox<SelectItem<Key>, "multiple">
      selectionMode="multiple"
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={setInputValue}
      // RAC only fires this when focus leaves the field, not when a row is
      // pressed, so a click on the list can't commit the partial text first
      onBlur={handleBlur}
      defaultItems={entries}
      defaultFilter={(text, search) =>
        (menuTrigger === "focus" || search.trim() !== "") &&
        filter(text, search)
      }
      menuTrigger={menuTrigger}
      allowsEmptyCollection={
        allowsEmptyCollection && (menuTrigger === "focus" || createName !== "")
      }
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      // a visible label already names the input; both would read it twice
      aria-label={label ? undefined : ariaLabel}
      className={styles.base({ className: classNames?.base })}
    >
      <SelectLabel label={label} />
      <RAGroup
        className={styles.inputWrapper({ className: classNames?.inputWrapper })}
        // capture runs before RAC's own keydown on the input commits the row
        onKeyDownCapture={(e) => {
          if (e.key === "Tab") isTabbingRef.current = true;
        }}
      >
        <RAComboBoxValue className="contents">
          {() =>
            value.length > 0 && (
              <RATagGroup
                aria-label={accessibleLabel}
                onRemove={(keys) =>
                  onChange(value.filter((name) => !keys.has(name)))
                }
                className="contents"
              >
                <RATagList className="contents">
                  {value.map((name) => (
                    <RATag
                      key={name}
                      id={name}
                      textValue={name}
                      className={styles.tag({ className: classNames?.tag })}
                    >
                      <span className="truncate">{name}</span>
                      <RAButton
                        slot="remove"
                        className="hover:bg-primary/10 flex shrink-0 cursor-pointer items-center justify-center rounded-full"
                      >
                        <CloseIcon aria-hidden width={14} height={14} />
                      </RAButton>
                    </RATag>
                  ))}
                </RATagList>
              </RATagGroup>
            )
          }
        </RAComboBoxValue>
        <RAInput
          placeholder={value.length === 0 ? placeholder : undefined}
          onKeyDownCapture={handleKeyDownCapture}
          onKeyDown={handleKeyDown}
          className={styles.input({ className: classNames?.input })}
        />
      </RAGroup>

      <RAPopover
        className={styles.popover({ className: classNames?.popover })}
        placement="bottom start"
      >
        <SelectListbox<Key, SelectItem<Key>>
          items={entries}
          aria-label={label ? undefined : ariaLabel}
          selectionMode="multiple"
          className="border-border max-h-64 border"
        />
      </RAPopover>

      {isInvalid && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
    </RAComboBox>
  );
}

function SingleCreatableComboBox({
  items,
  value,
  onChange,
  onCreate,
  label,
  "aria-label": ariaLabel,
  placeholder,
  errorMessage,
  isDisabled = false,
  classNames,
}: Readonly<CreatableComboBoxBaseProps & SingleSelectionProps>) {
  const labelOf = (key: Key | null) =>
    items.find((item) => item.value === key)?.label ?? "";
  const selectedLabel = labelOf(value);
  // null while not editing, so the input follows the selected label through
  // late loads and renames without ever overwriting typed text
  const [draft, setDraft] = useState<string | null>(null);
  const inputValue = draft ?? selectedLabel;
  const [isCreating, setIsCreating] = useState(false);
  const filter = useContainsFilter();
  const { entries, createName, allowsEmptyCollection } = useCreatableEntries(
    items,
    inputValue,
  );
  const isInvalid = !!errorMessage;
  const styles = creatableComboBox();

  const handleChange = async (key: Key | null) => {
    if (isCreating) return;
    if (key !== CREATE_KEY) {
      // with the input controlled RAC leaves the text to us, and it reports
      // the unchanged key when the field is left so typed text can revert
      setDraft(null);
      if (typeof key === "string" && key !== value) onChange(key);
      return;
    }
    setIsCreating(true);
    try {
      const created = await onCreate(createName);
      setDraft(null);
      onChange(created.value);
    } catch {
      // the caller reports the failure; the typed name stays for a retry
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <RAComboBox<SelectItem<Key>, "single">
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      // RAC skips its blur revert when the text already equals the label, so
      // such a draft would otherwise pin the old label past a rename
      onInputChange={(text) => setDraft(text === selectedLabel ? null : text)}
      defaultItems={entries}
      defaultFilter={filter}
      menuTrigger="focus"
      allowsEmptyCollection={allowsEmptyCollection}
      isDisabled={isDisabled}
      isReadOnly={isCreating}
      isInvalid={isInvalid}
      aria-label={label ? undefined : ariaLabel}
      className={styles.base({ className: classNames?.base })}
    >
      <SelectLabel label={label} />
      <RAGroup
        className={styles.inputWrapper({ className: classNames?.inputWrapper })}
      >
        <RAInput
          placeholder={placeholder}
          onKeyDown={preventEnterSubmit}
          aria-busy={isCreating || undefined}
          className={styles.input({ className: classNames?.input })}
        />
        {isCreating && <Spinner size="xs" className="shrink-0" />}
      </RAGroup>

      <RAPopover
        className={styles.popover({ className: classNames?.popover })}
        placement="bottom start"
      >
        <SelectListbox<Key, SelectItem<Key>>
          items={entries}
          aria-label={label ? undefined : ariaLabel}
          className="border-border max-h-64 border"
        />
      </RAPopover>

      {isInvalid && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
    </RAComboBox>
  );
}
